import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from '@iveco-ti/iveco-ds/hook-form'
import { CircleSlash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { createSwapy, SlotItemMapArray, Swapy, utils } from 'swapy'

import { Toast } from '@/common/components/Toast'
import { PostModel } from '@/common/models/post'
import { useNewsHighlighted } from './useNewsHighlighted'

const NEW_NEWS_ID = 'new-slider-news'

export function useNewsOrder() {
  const [slotItemMap, setSlotItemMap] = useState<SlotItemMapArray>(
    utils.initSlotItemMap<PostModel>([], 'id'),
  )
  const [list, setList] = useState<PostModel[]>([])

  const { t } = useTranslation('panel')
  const { newsHighlighted } = useNewsHighlighted()
  const swapy = useRef<Swapy | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { getValues, setValue } = useFormContext()

  const changeNewsOrder = useCallback(
    (order: SlotItemMapArray) => {
      const newPosition = order.findIndex(({ item }) => item === NEW_NEWS_ID)

      if (newPosition >= 0 && newPosition <= 9) {
        setValue('sliderSort', newPosition)
        return setSlotItemMap(order)
      }

      toast(Toast, {
        toastId: 'denied-news-position',
        data: {
          type: 'warning',
          icon: CircleSlash,
          title: t('news.drawer_create_news.order.denied_position'),
        },
      })
    },
    [t, setValue],
  )

  useEffect(() => {
    if (containerRef.current) {
      swapy.current = createSwapy(containerRef.current, {
        manualSwap: true,
      })
    }

    if (!swapy.current) {
      throw new Error('Swapy was not initialized')
    }

    swapy.current.onSwap((event) => {
      changeNewsOrder(event.newSlotItemMap.asArray)
    })

    swapy.current.onSwapEnd(() => {
      swapy?.current?.update()
    })

    return () => {
      swapy.current?.destroy()
    }
  }, [changeNewsOrder])

  useEffect(
    () =>
      utils.dynamicSwapy(
        swapy.current,
        list,
        'id',
        slotItemMap,
        setSlotItemMap,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list],
  )

  useEffect(() => {
    if (!newsHighlighted.length) return

    setList([
      {
        id: NEW_NEWS_ID,
        title:
          getValues('title') ||
          getValues('subtitle') ||
          t('news.drawer_create_news.order.news_untitled'),
      } as PostModel,
      ...newsHighlighted,
    ])
  }, [getValues, newsHighlighted, newsHighlighted.length, t])

  const newsOrder = useMemo(
    () => utils.toSlottedItems<PostModel>(list, 'id', slotItemMap),
    [list, slotItemMap],
  )

  return {
    containerRef,
    newsOrder,
  }
}
